import { Suspense } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";
import { Loading } from "@core/components";
import {
  LoginView,
  ForgetPwdView,
  ResetPwdView,
  AcceptInviteView,
  ChangePwdView,
  AcceptMemberInviteView,
  SelectWorkspaceView,
  RegisterView,
  NotFoundView,
} from "@modules/auth/views";
import { AuthGuard, RoleGuard } from "@modules/auth/guards";
import { WorkspaceLayout, WorkspaceConsole } from "@modules/workspace/layouts";
import { SalesManagerLayout } from "@modules/sales/layouts";
import {
  SalesWorkspacesView,
  SalesCreateWorkspaceView,
  WorkspaceDetailsView,
  ConvertToPaidView,
  SalesArchivedWorkspacesView,
} from "@modules/sales/views";
import {
  WorkspaceLicensesView,
  WorkspaceMembersView,
  WorkspaceActivityView,
  WorkspaceSettingsView,
  WorkspaceProjectsView,
  WorkspaceCreateProjectView,
} from "@modules/workspace/views";
import {
  ProjectsPortalView,
  ProjectTeamView,
  ProjectMembersView,
  ProjectGroupsView,
  ProjectSettingsView,
  ProjectActivityView,
} from "@modules/project/views";
import {
  ProjectWorkspaceLayout,
  ProjectRouteGuard,
  ProjectContextGuard,
} from "@modules/project";
import {
  RootFolderRedirect,
  DocumentsView,
  ManageAccessView,
  TrashView,
  ApprovalsView,
} from "@modules/docs";
import { DocumentViewerView, AnnotationViewerView } from "@modules/viewers";

const router = createBrowserRouter([
  // ─── Auth (public) ────────────────────────────────────────────────
  {
    path: "/",
    element: (
      <Suspense fallback={<Loading isLoading />}>
        <LoginView />
      </Suspense>
    ),
  },
  {
    path: "/forget-password",
    element: (
      <Suspense fallback={<Loading isLoading />}>
        <ForgetPwdView />
      </Suspense>
    ),
  },
  {
    path: "/reset-password",
    element: (
      <Suspense fallback={<Loading isLoading />}>
        <ResetPwdView />
      </Suspense>
    ),
  },
  {
    path: "/accept-invite",
    element: (
      <Suspense fallback={<Loading isLoading />}>
        <AcceptInviteView />
      </Suspense>
    ),
  },
  {
    path: "/set-password",
    element: (
      <Suspense fallback={<Loading isLoading />}>
        <ChangePwdView />
      </Suspense>
    ),
  },
  {
    path: "/accept-member-invite",
    element: (
      <Suspense fallback={<Loading isLoading />}>
        <AcceptMemberInviteView />
      </Suspense>
    ),
  },
  {
    // TODO: in-app password change — currently reuses set-password flow
    path: "/change-password",
    element: (
      <Suspense fallback={<Loading isLoading />}>
        <ChangePwdView />
      </Suspense>
    ),
  },

  {
    path: "/register",
    element: (
      <Suspense fallback={<Loading isLoading />}>
        <RegisterView />
      </Suspense>
    ),
  },

  // ─── Select Workspace ─────────────────────────────────────────────
  {
    element: <AuthGuard />,
    children: [
      {
        path: "/select-workspace",
        element: (
          <Suspense fallback={<Loading isLoading />}>
            <SelectWorkspaceView />
          </Suspense>
        ),
      },
    ],
  },

  // ─── Workspace ────────────────────────────────────────────────────
  {
    element: <AuthGuard />,
    children: [
      {
        path: "/workspace/:workspaceId",
        element: (
          <Suspense fallback={<Loading isLoading />}>
            <WorkspaceLayout />
          </Suspense>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="console/members" replace />,
          },
          {
            path: "console",
            element: <WorkspaceConsole />,
            children: [
              {
                index: true,
                element: <Navigate to="members" replace />,
              },
              {
                path: "members",
                element: (
                  <Suspense fallback={<Loading isLoading />}>
                    <WorkspaceMembersView />
                  </Suspense>
                ),
              },
              {
                path: "activity",
                element: (
                  <Suspense fallback={<Loading isLoading />}>
                    <WorkspaceActivityView />
                  </Suspense>
                ),
              },
              {
                path: "licenses",
                element: (
                  <Suspense fallback={<Loading isLoading />}>
                    <WorkspaceLicensesView />
                  </Suspense>
                ),
              },
              {
                path: "projects",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<Loading isLoading />}>
                        <WorkspaceProjectsView />
                      </Suspense>
                    ),
                  },
                  {
                    path: "create",
                    element: (
                      <Suspense fallback={<Loading isLoading />}>
                        <WorkspaceCreateProjectView />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: "settings",
                element: (
                  <Suspense fallback={<Loading isLoading />}>
                    <WorkspaceSettingsView />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "project-portal",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<Loading isLoading />}>
                    <ProjectsPortalView />
                  </Suspense>
                ),
              },
              {
                path: ":projectId",
                element: (
                  <Suspense fallback={<Loading isLoading />}>
                    <ProjectWorkspaceLayout />
                  </Suspense>
                ),
                children: [
                  {
                    index: true,
                    element: <Navigate to="team" replace />,
                  },
                  {
                    path: "team",
                    element: (
                      <ProjectRouteGuard>
                        <Suspense fallback={<Loading isLoading />}>
                          <ProjectTeamView />
                        </Suspense>
                      </ProjectRouteGuard>
                    ),
                    children: [
                      {
                        index: true,
                        element: <Navigate to="members" replace />,
                      },
                      {
                        path: "members",
                        element: (
                          <Suspense fallback={<Loading isLoading />}>
                            <ProjectMembersView />
                          </Suspense>
                        ),
                      },
                      {
                        path: "groups",
                        element: (
                          <Suspense fallback={<Loading isLoading />}>
                            <ProjectGroupsView />
                          </Suspense>
                        ),
                      },
                    ],
                  },
                  {
                    path: "activity",
                    element: (
                      <ProjectRouteGuard>
                        <ProjectActivityView />
                      </ProjectRouteGuard>
                    ),
                  },
                  {
                    path: "documents",
                    children: [
                      {
                        index: true,
                        element: (
                          <ProjectRouteGuard>
                            <RootFolderRedirect />
                          </ProjectRouteGuard>
                        ),
                      },
                      {
                        path: "manage-access/:id",
                        element: (
                          <ProjectRouteGuard>
                            <Suspense fallback={<Loading isLoading />}>
                              <ManageAccessView />
                            </Suspense>
                          </ProjectRouteGuard>
                        ),
                      },
                      {
                        path: ":folderId",
                        element: (
                          <ProjectRouteGuard>
                            <DocumentsView />
                          </ProjectRouteGuard>
                        ),
                      },
                    ],
                  },
                  {
                    path: "approvals",
                    element: (
                      <ProjectRouteGuard>
                        <ApprovalsView />
                      </ProjectRouteGuard>
                    ),
                  },
                  {
                    path: "trash",
                    element: (
                      <ProjectRouteGuard>
                        <Suspense fallback={<Loading isLoading />}>
                          <TrashView />
                        </Suspense>
                      </ProjectRouteGuard>
                    ),
                  },
                  {
                    path: "settings",
                    element: (
                      <ProjectRouteGuard>
                        <Suspense fallback={<Loading isLoading />}>
                          <ProjectSettingsView />
                        </Suspense>
                      </ProjectRouteGuard>
                    ),
                  },
                ],
              },
              {
                path: ":projectId/doc/:docId/viewer",
                element: (
                  <ProjectContextGuard>
                    <Suspense fallback={<Loading isLoading />}>
                      <DocumentViewerView />
                    </Suspense>
                  </ProjectContextGuard>
                ),
              },
              {
                path: ":projectId/doc/:docId/viewer/approvals",
                element: (
                  <ProjectContextGuard>
                    <Suspense fallback={<Loading isLoading />}>
                      <AnnotationViewerView />
                    </Suspense>
                  </ProjectContextGuard>
                ),
              },
            ],
          },
        ],
      },
    ],
  },

  // Sales Views
  {
    element: <AuthGuard />,
    children: [
      {
        element: <RoleGuard allowed="sales" />,
        children: [
          {
            element: (
              <Suspense fallback={<Loading isLoading />}>
                <SalesManagerLayout />
              </Suspense>
            ),
            children: [
              {
                path: "/sales-manager",
                element: (
                  <Suspense fallback={<Loading isLoading />}>
                    <SalesWorkspacesView />
                  </Suspense>
                ),
              },
              {
                path: "/sales-manager/archived-workspaces",
                element: (
                  <Suspense fallback={<Loading isLoading />}>
                    <SalesArchivedWorkspacesView />
                  </Suspense>
                ),
              },
              {
                path: "/sales-manager/create-workspace",
                element: (
                  <Suspense fallback={<Loading isLoading />}>
                    <SalesCreateWorkspaceView />
                  </Suspense>
                ),
              },
              {
                path: "/sales-manager/workspaces/:workspaceId",
                element: (
                  <Suspense fallback={<Loading isLoading />}>
                    <WorkspaceDetailsView />
                  </Suspense>
                ),
              },
              {
                path: "/sales-manager/workspaces/:workspaceId/convert",
                element: (
                  <Suspense fallback={<Loading isLoading />}>
                    <ConvertToPaidView />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
    ],
  },

  // ─── 404 ──────────────────────────────────────────────────────────
  {
    path: "*",
    element: (
      <Suspense fallback={<Loading isLoading />}>
        <NotFoundView />
      </Suspense>
    ),
  },
]);

const AppRoutes = () => <RouterProvider router={router} />;

export default AppRoutes;
